import { Tag } from '@ads/components-react';
import {
  AcademicCapIcon,
  BellIcon,
  ChevronRightIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentListIcon,
  DocumentCheckIcon,
  PresentationChartBarIcon,
  ShieldCheckIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { createFileRoute, Link } from '@tanstack/react-router';
import type { ParseKeys } from 'i18next';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '@/shared/store/hooks/usePermissions';

export const Route = createFileRoute('/_authenticated/')({
  component: HomePage,
});

interface NavCard {
  readonly to: string;
  readonly titleKey: ParseKeys<'common'>;
  readonly descriptionKey: ParseKeys<'common'>;
  readonly tagKey: ParseKeys<'common'>;
  readonly icon: React.ReactNode;
}

function HomePage() {
  const { t } = useTranslation('common');
  const { isRouteAllowed } = usePermissions();

  const cards: NavCard[] = [
    {
      to: '/access-management',
      titleKey: 'home.cards.accessManagement.title',
      descriptionKey: 'home.cards.accessManagement.description',
      tagKey: 'home.cards.accessManagement.tag',
      icon: <ShieldCheckIcon className="h-xxs w-xxs" />,
    },
    {
      to: '/exam/correction-dashboard',
      titleKey: 'home.cards.correctionDashboard.title',
      descriptionKey: 'home.cards.correctionDashboard.description',
      tagKey: 'home.cards.correctionDashboard.tag',
      icon: <ClipboardDocumentCheckIcon className="h-xxs w-xxs" />,
    },
    {
      to: '/question-library/',
      titleKey: 'home.cards.questionLibrary.title',
      descriptionKey: 'home.cards.questionLibrary.description',
      tagKey: 'home.cards.questionLibrary.tag',
      icon: <ClipboardDocumentListIcon className="h-xxs w-xxs" />,
    },
    {
      to: '/slide-library',
      titleKey: 'home.cards.slideEditor.title',
      descriptionKey: 'home.cards.slideEditor.description',
      tagKey: 'home.cards.slideEditor.tag',
      icon: <PresentationChartBarIcon className="h-xxs w-xxs" />,
    },
    {
      to: '/exams/',
      titleKey: 'home.cards.examManagement.title',
      descriptionKey: 'home.cards.examManagement.description',
      tagKey: 'home.cards.examManagement.tag',
      icon: <DocumentCheckIcon className="h-xxs w-xxs" />,
    },
    {
      to: '/persons/',
      titleKey: 'home.cards.personManagement.title',
      descriptionKey: 'home.cards.personManagement.description',
      tagKey: 'home.cards.personManagement.tag',
      icon: <UsersIcon className="h-xxs w-xxs" />,
    },
    {
      to: '/enrollments/',
      titleKey: 'home.cards.enrollments.title',
      descriptionKey: 'home.cards.enrollments.description',
      tagKey: 'home.cards.enrollments.tag',
      icon: <AcademicCapIcon className="h-xxs w-xxs" />,
    },
    {
      to: '/notification/',
      titleKey: 'home.cards.notification.title',
      descriptionKey: 'home.cards.notification.description',
      tagKey: 'home.cards.notification.tag',
      icon: <BellIcon className="h-xxs w-xxs" />,
    },
  ];

  return (
    <div className="flex flex-col gap-xs">
      {/* Welcome hero */}
      <div className="rounded-md border-thin border-ctx-layout-border bg-ctx-layout-surface px-xl py-xs shadow-level1">
        <p className="uppercase tracking-widest text-ctx-content-brand ts-caption-xxs">
          {t('home.welcomeLabel')}
        </p>
        <h1 className="mt-mini text-ctx-content-title ts-heading-lg">{t('home.title')}</h1>
        <p className="mt-nano text-ctx-content-placeholder ts-paragraph-xs">{t('home.subtitle')}</p>
      </div>

      {/* Modules section */}
      <div className="flex flex-col gap-xxxs">
        <h2 className="text-ctx-content-placeholder ts-heading-sm">{t('home.modulesLabel')}</h2>

        <div className="grid grid-cols-1 gap-md sm:grid-cols-2 lg:grid-cols-3">
          {cards
            .filter(card => isRouteAllowed(card.to))
            .map(card => (
              <Link
                key={card.to}
                to={card.to as never}
                search={{} as never}
                className="group flex flex-col overflow-hidden rounded-md border-thin border-ctx-layout-border bg-ctx-layout-surface shadow-level1 transition-shadow duration-200 hover:shadow-level3"
              >
                {/* Top accent stripe */}
                <div className="h-1 w-full bg-ctx-content-brand" />

                <div className="flex flex-col gap-xs p-sm">
                  {/* Icon */}
                  <div className="flex h-lg w-lg items-center justify-center rounded-sm bg-ctx-interactive-secondary text-ctx-content-brand">
                    {card.icon}
                  </div>

                  {/* Tag + Title + Description */}
                  <div className="flex flex-col gap-nano">
                    <Tag
                      label={t(card.tagKey)}
                      size="sm"
                      status="highlight"
                      custom={{ text: 'text-nowrap' }}
                    />
                    <h3 className="text-ctx-content-title ts-heading-xs">{t(card.titleKey)}</h3>
                    <p className="text-ctx-content-placeholder ts-paragraph-xs">
                      {t(card.descriptionKey)}
                    </p>
                  </div>

                  {/* CTA */}
                  <div className="flex items-center gap-nano text-ctx-content-brand ts-paragraph-xs">
                    <span>{t('home.cta')}</span>
                    <ChevronRightIcon className="group-hover:translate-x-1 h-xxxs w-xxxs transition-transform duration-150" />
                  </div>
                </div>
              </Link>
            ))}
        </div>
      </div>
    </div>
  );
}
